/**
 * Router 모듈
 * @module app/Routers
 * @author Olive
 */
import { useEffect, useState } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import LoginPage from '@/pages/app/login/Login';
import AuthCallbackPage from '@/pages/app/login/AuthCallbackPage';
import MainPage from '@/pages/app/main/MainPage';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import { ensureAppSession } from '@/shared/utils/CommonUtil';
import AdventurePage from '@/pages/app/adventure/AdventurePage';
import AppPage from '@/pages/app/AppPage';
import Achievements from '@/pages/app/mypage/Achievements';
import MyPage from '@/pages/app/mypage/MyPage';
import Quiz from '@/pages/app/adventure/Quiz';
import Record from '@/pages/app/record/Record';
import Book from '@/pages/app/book/Book';
import BookPerson from '@/pages/app/book/BookPerson';
import ChapterIntro from '@/pages/app/adventure/Chapter';
import Sources from '@/pages/app/mypage/Sources';
import FAQ from '@/pages/app/mypage/FAQ';
import Settings from '@/pages/app/mypage/Settings';
import CharacterChange from '@/pages/app/mypage/Character';
import Level from '@/pages/app/mypage/Level';
import StreakLearning from '@/pages/app/event/StreakLearning';

// 페이지를 새로 연 뒤 세션 확인을 마쳤는지 (이후 만료는 API 호출 시 401 → 토큰 갱신으로 처리)
let isSessionChecked = false;

// 보호된 레이아웃
// 토큰이 남아 있어도 만료됐을 수 있어, 처음 진입할 때 만료 여부를 확인(필요하면 갱신)한 뒤에 화면을 보여준다.
// 갱신까지 실패하면 logout()이 로그인 화면으로 보낸다.
const ProtectedLayout = () => {
  const accessToken = useAppAuthStore(state => state.accessToken);
  const [isChecked, setIsChecked] = useState(isSessionChecked);

  useEffect(() => {
    if (isChecked || !accessToken) return;
    ensureAppSession().then(isValid => {
      if (!isValid) return;
      isSessionChecked = true;
      setIsChecked(true);
    });
  }, []);

  if (!accessToken) return <Navigate to="/login" replace />;
  if (!isChecked) return null;
  return <AppPage />;
};

const Routers = createBrowserRouter(
  [
    {
      path: '/',
      children: [
        // ===== Public Routes (인증 불필요) =====
        { path: 'login', Component: LoginPage },
        // { path: 'intro', Component: IntroPage },
        { path: 'auth/callback', Component: AuthCallbackPage },

        // ===== Protected Routes (인증 필요) =====
        {
          element: <ProtectedLayout />,
          children: [
            { index: true, element: <Navigate to="/main" replace /> },
            { path: 'main', Component: MainPage },

            // Adventure 관련
            { path: 'adventure', Component: AdventurePage },
            { path: 'adventure/chapter', Component: ChapterIntro },
            { path: 'adventure/quiz', Component: Quiz },

            // MyPage 관련
            { path: 'mypage', Component: MyPage },
            { path: 'mypage/faq', Component: FAQ },
            { path: 'mypage/settings', Component: Settings },
            { path: 'mypage/sources', Component: Sources },
            { path: 'mypage/character', Component: CharacterChange },
            { path: 'mypage/level', Component: Level },
            { path: 'mypage/achievements', Component: Achievements },

            // 기록
            { path: 'record', Component: Record },

            // 이벤트
            { path: 'event/streak', Component: StreakLearning },

            // Book 관련
            { path: 'book', Component: Book },
            { path: 'book/person', Component: BookPerson },
          ],
        },
      ],
    },
  ],
  { basename: '/' },
);

export default Routers;
